// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, RefreshCw, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import EventSheetPage from "@/pages/EventSheetPage";
import PaymentSheetPage from "@/pages/live-sheets/PaymentSheetPage";
import RosterSheetPage from "@/pages/live-sheets/RosterSheetPage";
import PlayersSheetPage from "@/pages/live-sheets/PlayersSheetPage";
import CheckinSheetPage from "@/pages/live-sheets/CheckinSheetPage";
import CircuitSheetPage from "@/pages/live-sheets/CircuitSheetPage";
import EventSummarySheetPage from "@/pages/live-sheets/EventSummarySheetPage";
import { formatCalendarDate } from "@/lib/dates";

const CYAN = "#06B6D4";

const SCOPE_META: Record<string, { label: string; icon: string }> = {
  event: { label: "Events", icon: "📋" },
  roster: { label: "Rosters", icon: "👥" },
  players: { label: "Players", icon: "🏃" },
  payment: { label: "Payments", icon: "💰" },
  scheduling: { label: "Scheduling", icon: "📅" },
  summary: { label: "Summary", icon: "📊" },
  circuit: { label: "Circuit", icon: "🏀" },
  checkin: { label: "Check-In", icon: "✅" },
};
const SCOPE_ORDER = ["event", "roster", "players", "payment", "scheduling", "summary", "circuit", "checkin"];

const fmtDate = (s: any) => formatCalendarDate(s, "MMM d, yyyy");

function PermissionBadge({ permission }: { permission: string }) {
  const cls =
    permission === "edit"
      ? "bg-green-100 text-green-800"
      : permission === "comment"
      ? "bg-amber-100 text-amber-800"
      : "bg-slate-200 text-slate-700";
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{permission}</span>;
}

function EmbeddedSheet({ sheet }: { sheet: any }) {
  switch (sheet.scope) {
    case "event":
      return <EventSheetPage tokenOverride={sheet.token} embedded />;
    case "payment":
      return <PaymentSheetPage tokenOverride={sheet.token} embedded />;
    case "roster":
      return <RosterSheetPage tokenOverride={sheet.token} embedded />;
    case "players":
      return <PlayersSheetPage tokenOverride={sheet.token} embedded />;
    case "checkin":
      return <CheckinSheetPage tokenOverride={sheet.token} embedded />;
    case "circuit":
      return <CircuitSheetPage tokenOverride={sheet.token} embedded />;
    case "summary":
      return <EventSummarySheetPage tokenOverride={sheet.token} embedded />;
    default:
      return (
        <div className="p-8 text-center text-sm text-slate-500">
          The “{SCOPE_META[sheet.scope]?.label || sheet.scope}” sheet view is not available yet.
        </div>
      );
  }
}

export default function MySheetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const emailParam = searchParams.get("email");
  const storedEmail = typeof window !== "undefined" ? localStorage.getItem("sheet_viewer_email") : null;
  const email = (emailParam || storedEmail || "").toLowerCase().trim();

  const [emailDraft, setEmailDraft] = useState("");
  const [sheets, setSheets] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeScope, setActiveScope] = useState<string | null>(null);
  const [selectedToken, setSelectedToken] = useState<string | null>(null);

  // Global search
  const [globalQuery, setGlobalQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [searching, setSearching] = useState(false);

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const loadSheets = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    const { data } = await supabase.rpc("get_my_sheets", { p_email: email });
    const parsed = typeof data === "string" ? JSON.parse(data) : data;
    const arr = Array.isArray(parsed) ? parsed : [];
    setSheets(arr);
    setLoading(false);
  }, [email]);

  useEffect(() => {
    if (email) {
      localStorage.setItem("sheet_viewer_email", email);
      loadSheets();
    }
  }, [email, loadSheets]);

  // Scopes present, in fixed order
  const scopesPresent = useMemo(() => {
    if (!sheets) return [];
    const counts: Record<string, number> = {};
    sheets.forEach((s) => {
      counts[s.scope] = (counts[s.scope] || 0) + 1;
    });
    return SCOPE_ORDER.filter((sc) => counts[sc]).map((sc) => ({ scope: sc, count: counts[sc] }));
  }, [sheets]);

  // Default scope/token selection
  useEffect(() => {
    if (!sheets || sheets.length === 0) return;
    if (activeScope && sheets.some((s) => s.scope === activeScope)) return;
    const first = scopesPresent[0]?.scope;
    if (first) setActiveScope(first);
  }, [sheets, scopesPresent, activeScope]);

  const scopeSheets = useMemo(() => {
    if (!sheets || !activeScope) return [];
    return sheets
      .filter((s) => s.scope === activeScope)
      .sort((a, b) => new Date(b.event_start_date || 0).getTime() - new Date(a.event_start_date || 0).getTime());
  }, [sheets, activeScope]);

  useEffect(() => {
    if (scopeSheets.length === 0) {
      setSelectedToken(null);
      return;
    }
    if (!selectedToken || !scopeSheets.some((s) => s.token === selectedToken)) {
      setSelectedToken(scopeSheets[0].token);
    }
  }, [scopeSheets, selectedToken]);

  const selectedSheet = useMemo(
    () => sheets?.find((s) => s.token === selectedToken) || null,
    [sheets, selectedToken]
  );

  const selectScope = (scope: string) => {
    setActiveScope(scope);
    const first = sheets
      ?.filter((s) => s.scope === scope)
      .sort((a, b) => new Date(b.event_start_date || 0).getTime() - new Date(a.event_start_date || 0).getTime())[0];
    setSelectedToken(first?.token || null);
    setSearchResults(null);
  };

  const selectTab = (token: string) => {
    setSelectedToken(token);
    setSearchResults(null);
    requestAnimationFrame(() => {
      tabRefs.current[token]?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
    });
  };

  const tabLabel = (s: any) => {
    if (s.scope === "summary") return s.label || "Summary";
    if (s.scope === "circuit") return s.circuit_name || s.label || "Circuit";
    return `${fmtDate(s.event_start_date)} — ${s.event_name || s.label || "Event"}`.replace(/^ — /, "");
  };

  // ---- Global search across all events ----
  const runGlobalSearch = async () => {
    const q = globalQuery.toLowerCase().trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    if (!sheets) return;
    setSearching(true);
    const targets = sheets.filter((s) => s.scope === "event" || s.scope === "payment" || s.scope === "roster");
    const results: any[] = [];
    await Promise.all(
      targets.map(async (s) => {
        try {
          const rpc = s.scope === "roster" ? "get_roster_sheet_data" : "get_event_sheet_data";
          const { data } = await supabase.rpc(rpc, { p_token: s.token });
          const parsed = typeof data === "string" ? JSON.parse(data) : data;
          const rows = parsed?.registrations || parsed?.rosters || [];
          rows.forEach((r: any) => {
            const hay = [r.team_name, r.coach_first, r.coach_last, r.email]
              .filter(Boolean)
              .map((v: any) => String(v).toLowerCase());
            if (hay.some((v) => v.includes(q))) {
              results.push({
                sheet: s,
                team: r.team_name,
                coach: [r.coach_first, r.coach_last].filter(Boolean).join(" "),
                email: r.email,
              });
            }
          });
        } catch (_e) {
          /* skip failed token */
        }
      })
    );
    setSearchResults(results);
    setSearching(false);
  };

  const groupedResults = useMemo(() => {
    if (!searchResults) return [];
    const groups: Record<string, { name: string; hits: any[] }> = {};
    searchResults.forEach((hit) => {
      const key = hit.sheet.token;
      const name = hit.sheet.event_name || hit.sheet.label || "Event";
      if (!groups[key]) groups[key] = { name, hits: [] };
      groups[key].hits.push(hit);
    });
    return Object.values(groups);
  }, [searchResults]);

  const openHit = (sheet: any) => {
    setActiveScope(sheet.scope);
    setSelectedToken(sheet.token);
    setSearchResults(null);
  };

  // ---- Email entry screen ----
  if (!email) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-900">My Live Sheets</h1>
          <p className="mt-1 text-sm text-slate-500">Enter your email to access your assigned sheets</p>
          <form
            className="mt-6 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const lower = emailDraft.toLowerCase().trim();
              if (!lower) return;
              localStorage.setItem("sheet_viewer_email", lower);
              setSearchParams({ email: lower });
            }}
          >
            <input
              type="email"
              autoFocus
              className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm"
              placeholder="you@example.com"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
            />
            <button
              type="submit"
              className="h-10 rounded-md px-5 text-sm font-semibold text-white"
              style={{ backgroundColor: CYAN }}
            >
              Go
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-white">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b bg-white">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          {scopesPresent.map(({ scope, count }) => {
            const active = scope === activeScope;
            return (
              <button
                key={scope}
                onClick={() => selectScope(scope)}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  active ? "text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                style={active ? { backgroundColor: CYAN } : undefined}
              >
                <span>{SCOPE_META[scope]?.icon}</span>
                {SCOPE_META[scope]?.label || scope}
                <span className={`rounded-full px-1.5 text-xs ${active ? "bg-white/25" : "bg-slate-200"}`}>{count}</span>
              </button>
            );
          })}
          <div className="ml-auto flex flex-1 items-center gap-2" style={{ minWidth: 280 }}>
            <input
              className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm"
              placeholder="Search team, coach, or email across ALL events…"
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runGlobalSearch()}
            />
            <button
              onClick={runGlobalSearch}
              className="inline-flex h-9 items-center gap-1 rounded-md px-3 text-sm font-semibold text-white"
              style={{ backgroundColor: CYAN }}
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Go
            </button>
            <button
              onClick={loadSheets}
              className="inline-flex h-9 items-center gap-1 rounded-md border px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Bottom event tab strip */}
        {!searchResults && scopeSheets.length > 0 && (
          <div className="flex gap-1 overflow-x-auto border-b bg-slate-50 px-4">
            {scopeSheets.map((s) => {
              const active = s.token === selectedToken;
              return (
                <button
                  key={s.token}
                  ref={(el) => (tabRefs.current[s.token] = el)}
                  onClick={() => selectTab(s.token)}
                  className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm ${
                    active ? "font-bold" : "border-transparent text-slate-600 hover:text-slate-900"
                  }`}
                  style={active ? { borderColor: CYAN, color: CYAN } : undefined}
                >
                  {tabLabel(s)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading your sheets…
          </div>
        )}

        {!loading && sheets && sheets.length === 0 && (
          <div className="py-16 text-center text-slate-500">
            <p>No sheets are shared with {email}.</p>
            <button
              className="mt-3 text-sm font-medium underline"
              style={{ color: CYAN }}
              onClick={() => {
                localStorage.removeItem("sheet_viewer_email");
                setSearchParams({});
              }}
            >
              Try a different email
            </button>
          </div>
        )}

        {/* Global search results */}
        {!loading && searchResults && (
          <div className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {searchResults.length} result{searchResults.length === 1 ? "" : "s"} for “{globalQuery}”
              </p>
              <button
                className="text-sm underline"
                style={{ color: CYAN }}
                onClick={() => {
                  setSearchResults(null);
                  setGlobalQuery("");
                }}
              >
                Back to tabs
              </button>
            </div>
            {groupedResults.map((g) => (
              <div key={g.name} className="mb-4">
                <h3 className="mb-1 text-sm font-bold text-slate-800">{g.name}</h3>
                <div className="overflow-hidden rounded-md border">
                  {g.hits.map((hit, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-4 px-3 py-2 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}
                    >
                      <span className="w-48 truncate font-medium">{hit.team || "—"}</span>
                      <span className="w-40 truncate text-slate-600">{hit.coach || "—"}</span>
                      <span className="flex-1 truncate text-slate-600">{hit.email || "—"}</span>
                      <button
                        className="text-sm font-medium underline"
                        style={{ color: CYAN }}
                        onClick={() => openHit(hit.sheet)}
                      >
                        Open
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {searchResults.length === 0 && (
              <p className="text-center text-sm text-slate-400">No matches found.</p>
            )}
          </div>
        )}

        {/* Selected embedded sheet */}
        {!loading && !searchResults && selectedSheet && (
          <div>
            <div className="flex items-center gap-3 border-b bg-white px-4 py-3">
              <h2 className="text-lg font-bold text-slate-900">
                {selectedSheet.event_name || selectedSheet.circuit_name || selectedSheet.label}
              </h2>
              <PermissionBadge permission={selectedSheet.permission} />
            </div>
            <EmbeddedSheet sheet={selectedSheet} />
          </div>
        )}
      </div>
    </div>
  );
}
